import {
  useGetFailedShiftsJobsQuery,
  useGetFailedShiftsListQuery,
  useLazyDownloadShiftExcelQuery,
  useUploadShiftMutation,
} from '@/app/features/employee/emp-attendance/emp-attendance.api';
import type { FailedShiftsListData } from '@/app/features/employee/emp-attendance/emp-attendance.type';
import { BulkUploader, BulkUploadErrors } from '@/components/BulkUploader';
import useModal from '@/hooks/useModal';
import { toast } from '@/lib/toast';
import { Button, type TableProps } from 'antd';

type Props = {
  open: boolean;
  handleClose: (value?: boolean) => void;
};

const columns: TableProps<FailedShiftsListData>['columns'] = [
  {
    title: 'Shift Name',
    dataIndex: 'shiftName',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Start Time',
    dataIndex: ['rawShiftData', 'startTime'],
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'End Time',
    dataIndex: ['rawShiftData', 'endTime'],
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Grace Period',
    dataIndex: ['rawShiftData', 'checkInGracePeriod'],
    render: (val) => (val !== undefined ? `${val} min` : 'n/a'),
  },
  {
    title: 'Failure Reason',
    dataIndex: 'failureReason',
    render: (val) =>
      val ? (
        <span className="block min-w-[200px] text-red-400">{val}</span>
      ) : (
        'n/a'
      ),
  },
];

export default function ImportShift({ open, handleClose }: Props) {
  const [uploadShift, { isLoading: isUploading }] = useUploadShiftMutation();
  const [downloadTemplate] = useLazyDownloadShiftExcelQuery();

  const {
    open: errorOpen,
    handleOpen: errorHandleOpen,
    handleClose: errorClose,
  } = useModal<undefined, 'ERROR_SHOW'>();

  const handleDownloadTemplate = async () => {
    try {
      const blobUrl = await downloadTemplate().unwrap();
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'shift-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', 'Failed to download template.');
    }
  };

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    await uploadShift(formData).unwrap();
  };

  return (
    <>
      <BulkUploader
        open={open}
        onClose={() => handleClose()}
        title="Import Shift"
        subtitle="File Type Must be in .xlsx"
        accept=".xlsx"
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUpload}
        uploadLoading={isUploading}
        errorViewer={
          <Button
            type="default"
            className="!h-10 text-center text-teal-500 text-sm font-normal underline !bg-transparent !border-none !shadow-none"
            onClick={() => errorHandleOpen({ type: 'ERROR_SHOW' })}
          >
            View Error Table Data
          </Button>
        }
      />

      <BulkUploadErrors<FailedShiftsListData>
        open={errorOpen.open}
        onClose={errorClose}
        useGetJobsQuery={useGetFailedShiftsJobsQuery}
        useGetRecordsQuery={useGetFailedShiftsListQuery}
        columns={columns}
        jobsErrorMessage="Failed to get shift import error data"
        recordsErrorMessage="Failed to get shift import error data"
      />
    </>
  );
}
